import {
  useEffect,
  useState,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { config } from '@utility/config';
import PlacesAutocomplete, {
  geocodeByAddress,
} from 'react-places-autocomplete';
import { useJsApiLoader } from '@react-google-maps/api';
import { cn } from '@skupreme/shared/ui';
import { useAsyncDebounce } from '@hooks/debounceAsync';
import {
  parsePastedAddress,
  type GeocodedAddressComponent,
  type ParsedPastedAddress,
} from '@utility/address-parser';
import { FIELD_TEXTAREA } from './form-field/styles';
import { MuteLabel } from './form-field/chrome';

type SearchProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'onSelect'
> & {
  label?: ReactNode;
  setFormattedAddress: (address: ParsedPastedAddress) => void;
  /** The caller's current parse; accepted for its call sites, not read here. */
  formatedAddress?: unknown;
};

export default function Search({
  label,
  setFormattedAddress,
  formatedAddress: _formatedAddress,
  className,
  ...rest
}: SearchProps) {
  const [state, setState] = useState('');
  const finalSearch = useAsyncDebounce(state, 500);
  const handleChange = (address: string) => {
    setState(address);
  };

  useEffect(() => {
    onFinalSearch();
  }, [finalSearch]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: `${config.googlePlacesAPI}`,
    libraries: ['places'],
  });

  const changeAddress = (
    addressComponents: GeocodedAddressComponent[],
    completeAddress: string,
  ) => {
    setFormattedAddress(parsePastedAddress(addressComponents, completeAddress));
  };

  const onFinalSearch = async () => {
    if (!isLoaded) return;

    if (finalSearch) {
      const addressDetails = await geocodeByAddress(finalSearch);
      if (addressDetails !== null)
        changeAddress(addressDetails[0].address_components, finalSearch);
    }
  };

  const handleSelect = async (address: string) => {
    if (!isLoaded) return;

    setState(address);
    let addressDetails:
      | { address_components: GeocodedAddressComponent[] }[]
      | null = null;
    try {
      addressDetails = await geocodeByAddress(address);
    } catch (error) {
      console.log('error', error);
    }
    const passAddressIfCustom = address === state ? address : '';
    changeAddress(
      addressDetails?.[0].address_components || [],
      passAddressIfCustom,
    );
  };

  if (!isLoaded) {
    return (
      <div
        aria-hidden
        className="sk:flex sk:animate-pulse sk:flex-col sk:gap-2 sk:py-1"
      >
        <div className="sk:h-3 sk:rounded-sm sk:bg-fill-secondary" />
        <div className="sk:h-3 sk:w-2/3 sk:rounded-sm sk:bg-fill-secondary" />
      </div>
    );
  }

  return (
    <div>
      {label && <MuteLabel className="sk:mb-1">{label}</MuteLabel>}
      <PlacesAutocomplete
        value={state}
        onChange={handleChange}
        onSelect={handleSelect}
        debounce={1000}
      >
        {({ getInputProps, suggestions, loading, getSuggestionItemProps }) => {
          const arr = [...(suggestions || [])];
          /* What was typed stays choosable when Places has no exact match. */
          if (
            suggestions.length > 0 &&
            suggestions.findIndex(x => x.description === state) < 0
          ) {
            arr.push({ active: false, description: state });
          }
          const open = suggestions.length > 0;
          return (
            <div>
              <textarea
                {...getInputProps()}
                rows={5}
                {...rest}
                className={cn(
                  FIELD_TEXTAREA,
                  'sk:resize-none!',
                  open && 'sk:rounded-b-none!',
                  className,
                )}
              />
              {(open || loading) && (
                <div className="sk:overflow-hidden sk:rounded-b-card sk:bg-surface sk:inset-shadow-hairline sk:shadow-card">
                  {loading && (
                    <div className="sk:px-3 sk:py-1.5 sk:text-fg-tertiary">
                      Loading...
                    </div>
                  )}
                  {arr.map(suggestion => (
                    <div
                      {...getSuggestionItemProps(suggestion)}
                      key={
                        suggestion.placeId ?? `typed-${suggestion.description}`
                      }
                      className={cn(
                        'sk:cursor-pointer sk:px-3 sk:py-1.5 sk:text-dense sk:text-fg sk:hover:bg-fill-secondary',
                        suggestion.active && 'sk:bg-fill-secondary',
                      )}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }}
      </PlacesAutocomplete>
    </div>
  );
}
